import React, { useState } from 'react'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import {
  Stack,
  Typography,
  TextField,
  Grid,
  Box,
  FormControlLabel,
  Switch,
  CircularProgress,
  Alert,
  InputAdornment,
  Fab,
  Tooltip,
} from '@mui/material'
import { Search, Add } from '@mui/icons-material'
import { useEntriesQuery } from '../hooks/useFirebaseQueries'
import { selectUserIsAuthenticated } from '../store/slices/userSlice'
import FilterChip from '../components/FilterChip'
import BirdCard from '../components/Cards/BirdFlockManagerCard/BirdCard'

const FlockManagerPage = () => {
  const navigate = useNavigate()
  const isAuthenticated = useSelector(selectUserIsAuthenticated)
  const [searchQuery, setSearchQuery] = useState('')
  const [filterFavorites, setFilterFavorites] = useState(false)
  
  // Filter states - now arrays for multiple selections
  const [selectedBreeds, setSelectedBreeds] = useState([])
  const [selectedSpecies, setSelectedSpecies] = useState([])
  const [selectedMothers, setSelectedMothers] = useState([])
  const [selectedFathers, setSelectedFathers] = useState([])
  
  // Menu anchor states
  const [breedMenuAnchor, setBreedMenuAnchor] = useState(null)
  const [speciesMenuAnchor, setSpeciesMenuAnchor] = useState(null)
  const [motherMenuAnchor, setMotherMenuAnchor] = useState(null)
  const [fatherMenuAnchor, setFatherMenuAnchor] = useState(null)
  
  const { data: entries = [], isLoading, error } = useEntriesQuery()

  // Function to close all menus except the one being opened
  const handleMenuOpen = (openingFilterType) => {
    if (openingFilterType !== 'breed') setBreedMenuAnchor(null)
    if (openingFilterType !== 'species') setSpeciesMenuAnchor(null)
    if (openingFilterType !== 'mother') setMotherMenuAnchor(null)
    if (openingFilterType !== 'father') setFatherMenuAnchor(null)
  }

  const filteredEntries = entries.filter(entry => {
    try {
      const matchesSearch = !searchQuery || 
        Object.values(entry).some(value => 
          String(value).toLowerCase().includes(searchQuery.toLowerCase())
        )
      
      const matchesFilter = !filterFavorites || 
        (entry.isFavorited || entry.isFoundation)
      
      const matchesBreed = selectedBreeds.length === 0 || 
        (Array.isArray(entry.breed) 
          ? entry.breed.some(breed => selectedBreeds.includes(breed))
          : selectedBreeds.includes(entry.breed)
        )
      const matchesSpecies = selectedSpecies.length === 0 || selectedSpecies.includes(entry.species)
      const matchesMother = selectedMothers.length === 0 || selectedMothers.includes(entry.mother)
      const matchesFather = selectedFathers.length === 0 || selectedFathers.includes(entry.father)
      
      return matchesSearch && matchesFilter && matchesBreed && matchesSpecies && matchesMother && matchesFather
    } catch (error) {
      console.error('Error filtering entry:', entry, error)
      return true // Include entry if there's an error
    }
  })

  if (!isAuthenticated) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '50vh' 
      }}>
        <Alert severity="info">
          Please log in to view your flock.
        </Alert>
      </Box>
    )
  }

  if (isLoading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '50vh' 
      }}>
        <CircularProgress size={48} />
      </Box>
    )
  }

  if (error) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '50vh' 
      }}>
        <Alert severity="error">
          Error loading entries: {error.message}
        </Alert>
      </Box>
    )
  }

  const handleAddBird = () => {
    navigate('/add-entry')
  }

  return (
    <>
      <Stack spacing={3}>
        <Stack spacing={1}>
          <Typography variant="h4" component="h2">
            Flock Manager
          </Typography>
          <Typography variant="h6" color="text.secondary">
            Manage and organize your flock entries
          </Typography>
        </Stack>
      
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        gap: 2,
        flexDirection: { xs: 'column', md: 'row' }
      }}>
        {/* Left side - Filter chips */}
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
          <FilterChip
            filterType="breed"
            selectedValues={selectedBreeds}
            setSelectedValues={setSelectedBreeds}
            menuAnchor={breedMenuAnchor}
            setMenuAnchor={setBreedMenuAnchor}
            entries={entries}
            label="Breed"
            onMenuOpen={handleMenuOpen}
          />
          
          <FilterChip
            filterType="species"
            selectedValues={selectedSpecies}
            setSelectedValues={setSelectedSpecies}
            menuAnchor={speciesMenuAnchor}
            setMenuAnchor={setSpeciesMenuAnchor}
            entries={entries}
            label="Species"
            onMenuOpen={handleMenuOpen}
          />
          
          <FilterChip
            filterType="mother"
            selectedValues={selectedMothers}
            setSelectedValues={setSelectedMothers}
            menuAnchor={motherMenuAnchor}
            setMenuAnchor={setMotherMenuAnchor}
            entries={entries}
            label="Mother"
            onMenuOpen={handleMenuOpen}
          />
          
          <FilterChip
            filterType="father"
            selectedValues={selectedFathers}
            setSelectedValues={setSelectedFathers}
            menuAnchor={fatherMenuAnchor}
            setMenuAnchor={setFatherMenuAnchor}
            entries={entries}
            label="Father"
            onMenuOpen={handleMenuOpen}
          />
        </Box>

        {/* Right side - Search and favorites toggle */}
        <Box sx={{ 
          display: 'flex', 
          gap: 2, 
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'stretch', sm: 'center' },
          minWidth: { xs: '100%', md: 'auto' }
        }}>
          <TextField
            placeholder="Search entries..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            size="medium"
            sx={{ maxWidth: 300 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
          />
          
          <FormControlLabel
            control={
              <Switch
                checked={filterFavorites}
                onChange={(e) => setFilterFavorites(e.target.checked)}
                size="medium"
              />
            }
            label="Favorites & Foundation only"
          />
        </Box>
      </Box>


      {filteredEntries.length === 0 ? (
        <Alert severity="info">
          {entries.length === 0 
            ? "No entries found. Add your first entry to get started!"
            : "No entries match your search criteria."
          }
        </Alert>
      ) : (
        <Grid container spacing={2}>
          {filteredEntries.map((entry) => (
            <Grid size={{ xs: 12, sm: 6, md: 4 }} key={entry.entryId} sx={{ display: 'flex' }}>
              <BirdCard entry={entry} />
            </Grid>
          ))}
        </Grid>
      )}
      </Stack>

      {/* Floating Action Button for Desktop */}
      <Tooltip title="Add New Bird" placement="left">
        <Fab
          color="secondary"
          aria-label="add bird"
          onClick={handleAddBird}
          sx={{
            position: 'fixed',
            bottom: { xs: 96, md: 32 }, // Higher on mobile to avoid bottom nav
            right: 32,
            display: { xs: 'none', md: 'flex' }, // Only show on desktop
            zIndex: 1000,
            boxShadow: 3,
            '&:hover': {
              boxShadow: 6,
              transform: 'scale(1.05)',
            },
            transition: 'all 0.2s ease-in-out',
          }}
        >
          <Add />
        </Fab>
      </Tooltip>
    </>
  )
}

export default FlockManagerPage